import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
    Heart, ChatCircle, PaperPlaneRight, Image as ImageIcon,
    VideoCamera, X, Plus, Hash, Trash
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

const TAG_PRESETS = ['ride-highlight', 'build', 'trail', 'trick', 'meme', 'gear', 'ar-mode'];

function TimeAgo({ date }) {
    const diff = (Date.now() - new Date(date).getTime()) / 1000;
    if (diff < 60) return <span>just now</span>;
    if (diff < 3600) return <span>{Math.floor(diff / 60)}m ago</span>;
    if (diff < 86400) return <span>{Math.floor(diff / 3600)}h ago</span>;
    return <span>{Math.floor(diff / 86400)}d ago</span>;
}

function PostCard({ post, currentUserId, onLike, onDelete }) {
    const liked = (post.likes || []).includes(currentUserId);
    const [comments, setComments] = useState(post.comments || []);
    const [text, setText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const addComment = async () => {
        if (!text.trim()) return;
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/community/posts/${post.post_id}/comments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getH() },
                credentials: 'include',
                body: JSON.stringify({ text: text.trim() }),
            });
            if (res.ok) {
                const c = await res.json();
                setComments(prev => [...prev, c]);
                setText('');
            } else {
                toast.error('Failed to post comment');
            }
        } catch {
            toast.error('Failed to post comment');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
            <Card className="glass-card overflow-hidden" data-testid={`post-${post.post_id}`}>
                <CardContent className="p-4">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9 border border-primary/30">
                                <AvatarImage src={post.user_picture} />
                                <AvatarFallback className="bg-primary/20 text-primary text-xs font-bold">
                                    {(post.user_name || '?')[0].toUpperCase()}
                                </AvatarFallback>
                            </Avatar>
                            <div>
                                <p className="font-semibold text-sm text-foreground">{post.user_name}</p>
                                <p className="text-xs text-muted-foreground"><TimeAgo date={post.created_at} /></p>
                            </div>
                        </div>
                        {post.user_id === currentUserId && (
                            <button onClick={() => onDelete(post.post_id)}
                                className="text-muted-foreground hover:text-destructive transition-colors p-1"
                                data-testid={`delete-post-${post.post_id}`}>
                                <Trash size={16} />
                            </button>
                        )}
                    </div>

                    {/* Text */}
                    {post.text && (
                        <p className="text-sm text-foreground/90 whitespace-pre-wrap mb-3 leading-relaxed">{post.text}</p>
                    )}

                    {/* Media Grid */}
                    {post.media_urls?.length > 0 && (
                        <div className={`grid gap-1.5 mb-3 rounded-xl overflow-hidden ${post.media_urls.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                            {post.media_urls.slice(0, 4).map((url, i) => {
                                const src = url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
                                const isVideo = url.includes('.mp4') || url.includes('.webm') || url.includes('video');
                                return isVideo ? (
                                    <video key={i} src={src} controls className="w-full aspect-video object-cover rounded-lg" />
                                ) : (
                                    <img key={i} src={src} alt="" className={`w-full object-cover rounded-lg ${post.media_urls.length === 1 ? 'max-h-96' : 'aspect-square'}`} />
                                );
                            })}
                        </div>
                    )}

                    {/* Tags */}
                    {post.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                            {post.tags.map(tag => (
                                <Badge key={tag} variant="outline" className="text-[10px] border-primary/20 text-primary">
                                    <Hash size={10} className="mr-0.5" />{tag}
                                </Badge>
                            ))}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-5 py-2 border-t border-border/30">
                        <button onClick={() => onLike(post.post_id)}
                            className={`flex items-center gap-1.5 text-sm transition-colors ${liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-400'}`}
                            data-testid={`like-post-${post.post_id}`}>
                            <Heart size={18} weight={liked ? 'fill' : 'regular'} />
                            <span className="font-mono text-xs" data-testid={`like-count-${post.post_id}`}>{post.like_count || 0}</span>
                        </button>
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid={`comment-count-${post.post_id}`}>
                            <ChatCircle size={18} />
                            <span className="font-mono text-xs">{comments.length}</span>
                        </div>
                    </div>

                    {/* Inline Comments */}
                    <div className="space-y-2.5 pt-1" data-testid={`comments-${post.post_id}`}>
                        {comments.length === 0 ? (
                            <p className="text-xs text-muted-foreground/70 py-1">No comments yet — start the conversation.</p>
                        ) : (
                            comments.map(c => (
                                <div key={c.comment_id} className="flex gap-2.5" data-testid={`comment-${c.comment_id}`}>
                                    <Avatar className="h-6 w-6 mt-0.5 border border-border">
                                        <AvatarImage src={c.user_picture} />
                                        <AvatarFallback className="text-[9px] bg-muted">{(c.user_name || '?')[0]}</AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 bg-muted/30 rounded-xl px-3 py-1.5">
                                        <div className="flex items-baseline gap-2">
                                            <p className="text-xs font-semibold text-foreground">{c.user_name}</p>
                                            <p className="text-[10px] text-muted-foreground"><TimeAgo date={c.created_at} /></p>
                                        </div>
                                        <p className="text-sm text-foreground/80 whitespace-pre-wrap">{c.text}</p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Inline Composer */}
                    <div className="flex gap-2 mt-3">
                        <input
                            value={text}
                            onChange={e => setText(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && addComment()}
                            placeholder="Add a comment..."
                            className="flex-1 bg-muted/30 border border-border rounded-full px-4 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                            data-testid={`comment-input-${post.post_id}`}
                        />
                        <Button size="sm" onClick={addComment} disabled={submitting || !text.trim()}
                            className="rounded-full btn-primary" data-testid={`comment-submit-${post.post_id}`}>
                            <PaperPlaneRight size={16} />
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}

export default function Community() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCompose, setShowCompose] = useState(false);
    const [composeText, setComposeText] = useState('');
    const [composeTags, setComposeTags] = useState([]);
    const [mediaFiles, setMediaFiles] = useState([]);
    const [mediaPreviews, setMediaPreviews] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [activeTag, setActiveTag] = useState(null);
    const { user: authUser } = useAuth();
    const currentUserId = authUser?.user_id || '';

    const fetchPosts = useCallback(async () => {
        try {
            const url = activeTag ? `${API}/community/posts?tag=${activeTag}` : `${API}/community/posts`;
            const res = await fetch(url, { credentials: 'include', headers: getH() });
            if (res.ok) {
                const data = await res.json();
                setPosts(data.posts || []);
            }
        } catch {
            console.error('Failed to fetch posts');
        } finally {
            setLoading(false);
        }
    }, [activeTag]);

    useEffect(() => { fetchPosts(); }, [fetchPosts]);

    const handleLike = async (postId) => {
        try {
            const res = await fetch(`${API}/community/posts/${postId}/like`, {
                method: 'POST', credentials: 'include', headers: getH(),
            });
            if (res.ok) {
                const result = await res.json();
                setPosts(prev => prev.map(p =>
                    p.post_id === postId
                        ? {
                            ...p,
                            like_count: result.like_count,
                            likes: result.action === 'liked'
                                ? [...(p.likes || []), currentUserId]
                                : (p.likes || []).filter(id => id !== currentUserId)
                        }
                        : p
                ));
            }
        } catch {
            toast.error('Failed to like post');
        }
    };

    const handleDelete = async (postId) => {
        if (!window.confirm('Delete this post?')) return;
        try {
            const res = await fetch(`${API}/community/posts/${postId}`, {
                method: 'DELETE', credentials: 'include', headers: getH(),
            });
            if (res.ok) {
                setPosts(prev => prev.filter(p => p.post_id !== postId));
                toast.success('Post deleted');
            }
        } catch {
            toast.error('Failed to delete');
        }
    };

    const handleMediaSelect = (e) => {
        const files = Array.from(e.target.files).slice(0, 10 - mediaFiles.length);
        setMediaFiles(prev => [...prev, ...files]);
        files.forEach(f => {
            const reader = new FileReader();
            reader.onload = () => setMediaPreviews(prev => [...prev, { url: reader.result, type: f.type }]);
            reader.readAsDataURL(f);
        });
    };

    const removeMedia = (idx) => {
        setMediaFiles(prev => prev.filter((_, i) => i !== idx));
        setMediaPreviews(prev => prev.filter((_, i) => i !== idx));
    };

    const handleSubmitPost = async () => {
        if (!composeText.trim() && mediaFiles.length === 0) {
            toast.error('Write something or add media');
            return;
        }
        setUploading(true);
        try {
            const uploadedUrls = [];
            for (const file of mediaFiles) {
                const formData = new FormData();
                formData.append('file', file);
                const upRes = await fetch(`${API}/upload/media`, {
                    method: 'POST', credentials: 'include', headers: getH(), body: formData,
                });
                if (upRes.ok) {
                    const result = await upRes.json();
                    uploadedUrls.push(result.url);
                }
            }

            const res = await fetch(`${API}/community/posts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getH() },
                credentials: 'include',
                body: JSON.stringify({ text: composeText.trim(), media_urls: uploadedUrls, tags: composeTags }),
            });

            if (res.ok) {
                toast.success('Post shared!');
                setShowCompose(false);
                setComposeText('');
                setComposeTags([]);
                setMediaFiles([]);
                setMediaPreviews([]);
                fetchPosts();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Failed to create post');
            }
        } catch {
            toast.error('Failed to create post');
        } finally {
            setUploading(false);
        }
    };

    const toggleTag = (tag) => {
        setComposeTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev.slice(0, 4), tag]);
    };

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                {/* Header */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <span className="label-text text-primary text-glow">COMMUNITY</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground" data-testid="community-title">
                        Rider Feed
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm" style={{ fontFamily: 'var(--font-body)' }}>
                        Share highlights, builds, and ride moments with the crew
                    </p>
                </motion.div>

                {/* Tag Filters */}
                <div className="flex gap-2 overflow-x-auto pb-3 mb-4 scrollbar-hide">
                    <Button size="sm" variant={!activeTag ? 'default' : 'outline'}
                        className={`rounded-full text-xs whitespace-nowrap ${!activeTag ? 'btn-primary' : ''}`}
                        onClick={() => setActiveTag(null)} data-testid="filter-all">
                        All
                    </Button>
                    {TAG_PRESETS.map(tag => (
                        <Button key={tag} size="sm" variant={activeTag === tag ? 'default' : 'outline'}
                            className={`rounded-full text-xs whitespace-nowrap ${activeTag === tag ? 'btn-primary' : ''}`}
                            onClick={() => setActiveTag(activeTag === tag ? null : tag)} data-testid={`filter-${tag}`}>
                            <Hash size={12} className="mr-1" />{tag}
                        </Button>
                    ))}
                </div>

                {/* Compose */}
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
                    {!showCompose ? (
                        <Card className="glass-card mb-6 cursor-pointer hover:border-primary/30 transition-colors"
                            onClick={() => setShowCompose(true)} data-testid="compose-trigger">
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                                    <Plus size={18} className="text-primary" />
                                </div>
                                <span className="text-muted-foreground text-sm">Share a ride highlight, build, or moment...</span>
                            </CardContent>
                        </Card>
                    ) : (
                        <Card className="glass-card mb-6 border-primary/30" data-testid="compose-form">
                            <CardContent className="p-4 space-y-3">
                                <textarea value={composeText} onChange={e => setComposeText(e.target.value)}
                                    placeholder="What's happening on your ride?"
                                    className="w-full bg-transparent border-none text-foreground placeholder:text-muted-foreground text-sm resize-none focus:outline-none min-h-[80px]"
                                    autoFocus maxLength={2000} data-testid="compose-text" />

                                {mediaPreviews.length > 0 && (
                                    <div className="flex gap-2 overflow-x-auto pb-2">
                                        {mediaPreviews.map((m, i) => (
                                            <div key={i} className="relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden">
                                                {m.type?.startsWith('video') ? (
                                                    <video src={m.url} className="w-full h-full object-cover" />
                                                ) : (
                                                    <img src={m.url} alt="" className="w-full h-full object-cover" />
                                                )}
                                                <button onClick={() => removeMedia(i)} className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5">
                                                    <X size={12} className="text-white" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex flex-wrap gap-1.5">
                                    {TAG_PRESETS.map(tag => (
                                        <Badge key={tag} variant="outline"
                                            className={`text-[10px] cursor-pointer transition-colors ${composeTags.includes(tag) ? 'bg-primary/15 text-primary border-primary/30' : 'border-border text-muted-foreground hover:border-primary/20'}`}
                                            onClick={() => toggleTag(tag)}>
                                            <Hash size={10} className="mr-0.5" />{tag}
                                        </Badge>
                                    ))}
                                </div>

                                <div className="flex items-center justify-between pt-2">
                                    <div className="flex gap-2">
                                        <label className="cursor-pointer text-muted-foreground hover:text-primary transition-colors">
                                            <ImageIcon size={20} />
                                            <input type="file" accept="image/*" multiple className="hidden" onChange={handleMediaSelect} data-testid="media-image-input" />
                                        </label>
                                        <label className="cursor-pointer text-muted-foreground hover:text-primary transition-colors">
                                            <VideoCamera size={20} />
                                            <input type="file" accept="video/*" className="hidden" onChange={handleMediaSelect} data-testid="media-video-input" />
                                        </label>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button size="sm" variant="ghost"
                                            onClick={() => { setShowCompose(false); setComposeText(''); setComposeTags([]); setMediaFiles([]); setMediaPreviews([]); }}
                                            data-testid="compose-cancel">
                                            Cancel
                                        </Button>
                                        <Button size="sm" className="btn-primary" onClick={handleSubmitPost}
                                            disabled={uploading || (!composeText.trim() && mediaFiles.length === 0)} data-testid="compose-submit">
                                            {uploading ? 'Posting...' : 'Post'}
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </motion.div>

                {/* Feed */}
                <div className="space-y-4">
                    {loading ? (
                        [1, 2, 3].map(i => (
                            <Card key={i} className="glass-card">
                                <CardContent className="p-4 space-y-3">
                                    <div className="flex items-center gap-3">
                                        <Skeleton className="h-9 w-9 rounded-full" />
                                        <div className="space-y-1.5">
                                            <Skeleton className="h-3 w-24" />
                                            <Skeleton className="h-2.5 w-16" />
                                        </div>
                                    </div>
                                    <Skeleton className="h-16 w-full" />
                                    <Skeleton className="h-48 w-full rounded-xl" />
                                </CardContent>
                            </Card>
                        ))
                    ) : posts.length === 0 ? (
                        <Card className="glass-card">
                            <CardContent className="p-8 text-center">
                                <ChatCircle size={48} className="text-muted-foreground mx-auto mb-4" />
                                <p className="font-medium text-foreground">No posts yet</p>
                                <p className="text-sm text-muted-foreground mt-1">Be the first to share a ride highlight!</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <AnimatePresence>
                            {posts.map(post => (
                                <PostCard key={post.post_id} post={post} currentUserId={currentUserId}
                                    onLike={handleLike} onDelete={handleDelete} />
                            ))}
                        </AnimatePresence>
                    )}
                </div>
            </div>
        </Layout>
    );
}
